import { useCallback, useContext } from 'react'

import { Link } from '@/components/ContentStack/Link'
import { translateText } from '@/lib/translate'
import { LanguageContext, TranslationContext } from '@/providers/TranslationContextProvider'

import type React from 'react'

const AUTO_LINK_PLACEHOLDER_REGEX = /\{\{(link:([^{}]+))\}\}/g

const createAutoLinkValues = (text?: string): Record<string, React.ReactNode> | undefined => {
  if (text == null) {
    return undefined
  }

  const matches = text.matchAll(AUTO_LINK_PLACEHOLDER_REGEX)
  const values: Record<string, React.ReactNode> = {}
  let hasMatches = false

  for (const [placeholderWithBraces, placeholder, internalNameRaw] of matches) {
    const internalName = internalNameRaw.trim()

    if (!internalName) {
      continue
    }

    hasMatches = true
    values[placeholder] = <Link key={`translation-link-${placeholderWithBraces}`} internalName={internalName} />
  }

  return hasMatches ? values : undefined
}

export function useTranslations() {
  const trans = useContext(TranslationContext)
  const { language } = useContext(LanguageContext)

  const translate = useCallback(
    (key: string, values?: Record<string, React.ReactNode>) => {
      const text = trans?.find((translation) => translation.key === key)?.text?.[language]
      const autoLinkValues = createAutoLinkValues(text)
      const mergedValues = autoLinkValues != null ? { ...autoLinkValues, ...values } : values

      return translateText({ translations: trans, language, key, values: mergedValues })
    },
    [language, trans]
  )

  return { translate, language }
}
